import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { 
  BarChart3, 
  Download, 
  Calendar, 
  TrendingUp, 
  Users, 
  IndianRupee,
  PieChart,
  FileText,
  RefreshCw,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell, LineChart, Line, Legend, Area, AreaChart
} from 'recharts';

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

export const ReportsPage = () => {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sips, setSips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [selectedClient, setSelectedClient] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, clientsRes, ordersRes, sipsRes] = await Promise.all([
        axios.get(`${API}/bse/dashboard/stats`),
        axios.get(`${API}/bse/clients?limit=200`),
        axios.get(`${API}/bse/orders?limit=500`),
        axios.get(`${API}/bse/sip?active_only=false`)
      ]);
      
      setStats(statsRes.data);
      setClients(clientsRes.data.clients || []);
      setOrders(ordersRes.data.orders || []);
      setSips(sipsRes.data.sips || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate analytics
  const calculateAnalytics = () => {
    // Client onboarding over time (by month)
    const clientsByMonth = {};
    clients.forEach(client => {
      if (client.created_at) {
        const month = new Date(client.created_at).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        clientsByMonth[month] = (clientsByMonth[month] || 0) + 1;
      }
    });
    const clientGrowthData = Object.entries(clientsByMonth).map(([month, count]) => ({ month, count }));

    // Order distribution by type
    const purchaseOrders = orders.filter(o => o.transaction_type === 'P').length;
    const redemptionOrders = orders.filter(o => o.transaction_type === 'R').length;
    const orderTypeData = [
      { name: 'Purchase', value: purchaseOrders, color: '#22c55e' },
      { name: 'Redemption', value: redemptionOrders, color: '#ef4444' }
    ];

    // Order status distribution
    const statusCounts = {};
    orders.forEach(order => {
      statusCounts[order.status] = (statusCounts[order.status] || 0) + 1;
    });
    const orderStatusData = Object.entries(statusCounts).map(([status, count]) => ({ 
      status: status.charAt(0).toUpperCase() + status.slice(1), 
      count 
    }));

    // SIP analysis
    const activeSIPs = sips.filter(s => s.is_active);
    const totalSIPAmount = activeSIPs.reduce((sum, s) => sum + (s.amount || 0), 0);
    const sipByFrequency = {};
    activeSIPs.forEach(sip => {
      sipByFrequency[sip.frequency] = (sipByFrequency[sip.frequency] || 0) + 1;
    });
    const sipFrequencyData = Object.entries(sipByFrequency).map(([freq, count]) => ({ name: freq, value: count }));

    // Transaction volume by month
    const ordersByMonth = {};
    orders.forEach(order => {
      if (order.created_at) {
        const month = new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        if (!ordersByMonth[month]) {
          ordersByMonth[month] = { month, purchases: 0, redemptions: 0, total: 0 };
        }
        const amount = order.amount || 0;
        if (order.transaction_type === 'P') {
          ordersByMonth[month].purchases += amount;
        } else {
          ordersByMonth[month].redemptions += amount;
        }
        ordersByMonth[month].total += amount;
      }
    });
    const transactionVolumeData = Object.values(ordersByMonth);

    // Client registration status
    const registeredClients = clients.filter(c => c.bse_registration_status === 'registered').length;
    const pendingClients = clients.filter(c => c.bse_registration_status === 'pending').length;
    const clientStatusData = [
      { name: 'Registered', value: registeredClients, color: '#22c55e' },
      { name: 'Pending', value: pendingClients, color: '#f59e0b' }
    ];

    return {
      clientGrowthData,
      orderTypeData,
      orderStatusData,
      sipFrequencyData,
      transactionVolumeData,
      clientStatusData,
      totalSIPAmount,
      activeSIPCount: activeSIPs.length
    };
  };

  const analytics = !loading ? calculateAnalytics() : null;

  const exportReport = (type) => {
    toast.success(`Exporting ${type} report...`);
    // In production, this would generate a PDF/Excel file
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Reports & Analytics</h1>
            <p className="text-muted-foreground mt-1">Business insights and performance metrics</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" onClick={() => exportReport('summary')}>
              <Download className="w-4 h-4 mr-2" />
              Export Summary
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total AUM</p>
                  <p className="text-3xl font-bold text-foreground">
                    ₹{(stats?.aum?.total || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                    <ArrowUp className="w-3 h-3" /> Growing
                  </p>
                </div>
                <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center">
                  <IndianRupee className="w-7 h-7 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500/20 to-green-500/5 border-green-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Clients</p>
                  <p className="text-3xl font-bold text-foreground">{clients.length}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stats?.clients?.active || 0} active</p>
                </div>
                <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Users className="w-7 h-7 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500/20 to-blue-500/5 border-blue-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Monthly SIP Book</p>
                  <p className="text-3xl font-bold text-foreground">
                    ₹{analytics?.totalSIPAmount?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{analytics?.activeSIPCount || 0} active SIPs</p>
                </div>
                <div className="w-14 h-14 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-7 h-7 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/20 to-purple-500/5 border-purple-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                  <p className="text-3xl font-bold text-foreground">{orders.length}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stats?.orders?.pending || 0} pending</p>
                </div>
                <div className="w-14 h-14 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <FileText className="w-7 h-7 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="bg-card/50 border border-border/50">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="clients">Clients</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="sips">SIPs</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Client Growth Chart */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">Client Onboarding Trend</CardTitle>
                  <CardDescription>New clients added over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    {analytics?.clientGrowthData?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={analytics.clientGrowthData}>
                          <defs>
                            <linearGradient id="colorClients" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis dataKey="month" stroke="#888" fontSize={12} />
                          <YAxis stroke="#888" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                            labelStyle={{ color: '#fff' }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="count" 
                            stroke="#6366f1" 
                            fillOpacity={1}
                            fill="url(#colorClients)"
                            name="New Clients"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-muted-foreground">
                        No data available
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Order Type Distribution */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">Order Distribution</CardTitle>
                  <CardDescription>Purchase vs Redemption orders</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    {analytics?.orderTypeData?.some(d => d.value > 0) ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={analytics.orderTypeData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {analytics.orderTypeData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                          />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-muted-foreground">
                        No orders yet
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Client Registration Status */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">Registration Status</CardTitle>
                  <CardDescription>BSE registration breakdown</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={analytics?.clientStatusData || []}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {analytics?.clientStatusData?.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                        />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Client List Summary */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">Recent Clients</CardTitle>
                  <CardDescription>Latest client registrations</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {clients.slice(0, 10).map(client => (
                      <div key={client.client_id} className="flex items-center justify-between p-2 bg-background/50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{client.first_name} {client.last_name}</p>
                          <p className="text-xs text-muted-foreground">{client.pan}</p>
                        </div>
                        <Badge className={
                          client.bse_registration_status === 'registered' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                        }>
                          {client.bse_registration_status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="space-y-4">
            <div className="grid grid-cols-1 gap-6">
              {/* Transaction Volume */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">Transaction Volume</CardTitle>
                  <CardDescription>Monthly purchase and redemption amounts</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    {analytics?.transactionVolumeData?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.transactionVolumeData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis dataKey="month" stroke="#888" fontSize={12} />
                          <YAxis stroke="#888" fontSize={12} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                            formatter={(value) => `₹${value.toLocaleString()}`}
                          />
                          <Legend />
                          <Bar dataKey="purchases" fill="#22c55e" name="Purchases" />
                          <Bar dataKey="redemptions" fill="#ef4444" name="Redemptions" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-muted-foreground">
                        No transaction data available
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Order Status Distribution */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">Order Status Distribution</CardTitle>
                  <CardDescription>Orders by processing status</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    {analytics?.orderStatusData?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.orderStatusData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis type="number" stroke="#888" fontSize={12} />
                          <YAxis type="category" dataKey="status" stroke="#888" fontSize={12} width={100} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                          />
                          <Bar dataKey="count" fill="#6366f1" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-muted-foreground">
                        No orders yet
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* SIPs Tab */}
          <TabsContent value="sips" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* SIP by Frequency */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">SIP by Frequency</CardTitle>
                  <CardDescription>Distribution of SIP frequencies</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    {analytics?.sipFrequencyData?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={analytics.sipFrequencyData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                            label={({ name, value }) => `${name}: ${value}`}
                          >
                            {analytics.sipFrequencyData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                          />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-muted-foreground">
                        No SIPs yet
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* SIP Summary */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">SIP Summary</CardTitle>
                  <CardDescription>Key SIP metrics</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-background/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Active SIPs</p>
                          <p className="text-xl font-bold">{analytics?.activeSIPCount || 0}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-background/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                          <IndianRupee className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Monthly SIP Amount</p>
                          <p className="text-xl font-bold">₹{analytics?.totalSIPAmount?.toLocaleString() || 0}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-background/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Yearly SIP Value</p>
                          <p className="text-xl font-bold">₹{((analytics?.totalSIPAmount || 0) * 12).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
